import type { ListingList } from "../../post/types";
import { ListingType } from "../../../types/userListing";

export const normalizeListingType = (listingType?: string): string =>
    (listingType ?? "").toLowerCase().replace(/\s/g, "");

export const isNicheVideoListing = (listingType?: string): boolean =>
    normalizeListingType(listingType) === ListingType.NicheVideo;

// The campaign dropdown's value, "" when nothing is picked: undefined makes Radix Select
// uncontrolled, so after a clear it falls back to the first pick it stored itself.
export const listingSelectValue = (listing?: Pick<ListingList, "campaignId" | "listingType">): string =>
    listing ? `${listing.campaignId}|||${listing.listingType}` : "";

// Listing types allowed in the dropdown when "Post Captioned Video" is checked.
// Captions are sourced from a real video, so only listings that ship video media
// (MLS, Rentcast), use brand video (MyBrand), or are recorded (My Niche Video).
const CAPTIONED_VIDEO_ALLOWED_LISTING_TYPES = new Set<string>([
    "mlslisting",
    "rentcastlisting",
    "mybrand",
    ListingType.NicheVideo,
]);

export const isListingAllowedForCaptionedVideo = (listingType: string): boolean =>
    CAPTIONED_VIDEO_ALLOWED_LISTING_TYPES.has(normalizeListingType(listingType));

// The "Choose an Active Campaign" filter. My Niche Video exists only inside
// the captioned flow; a selection the picker no longer shows is cleared.
export const isListingShownInPicker = (listingType: string, postCaptionedVideo: boolean): boolean =>
    postCaptionedVideo ? isListingAllowedForCaptionedVideo(listingType) : !isNicheVideoListing(listingType);

// Clone the My Brand entry as "My Niche Video", the way "Text Only Post" is
// cloned: same campaign, so downstream it is a My Brand captioned video.
export const withNicheVideoEntry = (listings: ListingList[], eligible: boolean): ListingList[] => {
    if (!eligible || listings.some((l) => isNicheVideoListing(l.listingType))) return listings;
    const myBrand = listings.find((l) => normalizeListingType(l.listingType) === "mybrand");
    return myBrand
        ? [...listings, { ...myBrand, listingType: ListingType.NicheVideo, address: "My Niche Video", displayName: "My Niche Video" }]
        : listings;
};

// AI Enhance checkbox: hidden for Lead Generation, Text Only and My Niche
// Video (always on: the post text comes from the transcript), and for My
// Brand outside the captioned flow.
export const showsAiEnhanceToggle = (listingType: string, postCaptionedVideo: boolean): boolean => {
    const type = normalizeListingType(listingType);
    if (type === "leadgeneration" || type === "textonly" || type === ListingType.NicheVideo) return false;
    return type !== "mybrand" || postCaptionedVideo;
};
